import { createContext, useContext } from 'react';
import { configure } from 'mobx';

import { GroupProposalsTableStore } from 'store/ui-stores/group-info/groupProposalsTableStore';
import { ReviewSubmitStore } from 'store/ui-stores/review-submit/reviewSubmitStore';
import { DomainStoreV2 } from 'store/domain/domainStoreV2';
import BrowsingCriteriaModalStore from 'store/ui-stores/browsing/browsingCriteriaModalStore';
import { SaveProposalModalStore } from 'store/ui-stores/browsing/saveProposalModalStore';
import { ProposalItemsStore } from 'store/ui-stores/proposal-v2/proposalItemsStore';
import PortfolioConfigModalStore from 'store/ui-stores/proposal-v2/portfolioConfigModalStore';
import CustomAuthorizedUserStore from 'store/ui-stores/web-form/customAuthorizedUserStore';
import CustomReplicatedInfoStore from 'store/ui-stores/web-form/customReplicatedInfoStore';
import CustomPlanRateCapStore from 'store/ui-stores/proposal-v2/customPlanRateCapStore';
import { GroupInfoModalStore } from './ui-stores/group-info/groupInfoModalStore';
import { GroupNotesModalStore } from './ui-stores/group-info/groupNotesModalStore';
import { BrowsingModalsStore } from './ui-stores/group-info/browsingModalsStore';
import { SearchGroupsModalStore } from './ui-stores/dashboard/searchGroupsModalStore';
import { GroupStore } from './domain/groupStore';
import { AgentsStore } from './domain/agentsStore';
import { AssignAgentsModalStore } from './ui-stores/group-info/assignAgentsModalStore';
import { GeoStore } from './domain/geoStore';
import { EnrollmentStore } from './domain/enrollmentStore';
import { UserStore } from './domain/userStore';
import { GroupsDashboardController } from './controllers/groupsDashboardController';
import { GroupInfoController } from './controllers/groupInfoController';
import { GroupSidebarStore } from './ui-stores/group-info/groupSidebarStore';
import WebFormStore from './ui-stores/web-form/webFormStore';
import ExitModalStore from './general/exitModalStore';
import { EmployeeElectionsStore } from './ui-stores/elections/employeeElectionsStore';
import { ElectionStore } from './domain/electionStore';
import EditPrimaryModalStore from './ui-stores/elections/editPrimaryModalStore';
import { AttachmentsStore } from './ui-stores/group-info/attachmentsStore';
import { EnrollmentCardStore } from './ui-stores/group-info/enrollmentCardStore';
import { InsightsStore } from './ui-stores/insights/insightsStore';
import { CsvStore } from './domain/csvStore';
import { BookOfBusinessStore } from './ui-stores/book-of-business/bookOfBusinessStore';
import { SummaryTableStore } from './ui-stores/summary/summaryTableStore';
import InviteModalStore from './ui-stores/elections/inviteModalStore';
import SubscriberStore from './ui-stores/elections/subscriberStore';
import { AdditionalAccessStore } from './ui-stores/group-info/additionalAccessStore';
import EeClassesStore from './ui-stores/elections/eeClassesStore';
import BrowsingStore from './ui-stores/browsing/browsingStore';
import BrowsingFilterStore from './ui-stores/browsing/browsingFilterStore';
import { CartStoreV2 } from './domain/cartStoreV2';
import ComparisonStore from './ui-stores/comparison/comparisonStore';
import { FavoriteProposalsStore } from './ui-stores/favorite-proposals/favoriteProposalsStore';
import TemplatesModalStore from './ui-stores/group-info/templatesModalStore';
import { PdfStore } from './domain/pdfStore';
import { UpdateProposalModalStore } from './ui-stores/group-info/updateProposalModalStore';
import { CarrierFileStore } from './domain/carrierFileStore';
import UploadSyncFilesStore from './ui-stores/upload-sync-files/uploadSyncFilesStore';
import TriggerRenewalsStore from './ui-stores/trigger-renewals/triggerRenewalsStore';
import { EmailStore } from './domain/emailStore';
import FileExchangeStore from './ui-stores/files-tracking/fileExchangeStore';
import ExchangeStore from './domain/exchangeStore';
import BrowsingPortfolioConfigModalStore from './ui-stores/comparison/browsingPortfolioConfigModalStore';
import AdditionalOutputsStore from './ui-stores/additional-outputs/additionalOutputsStore';
import EdiStore from './domain/ediStore';
import ReadyToSendPdfsStore from './ui-stores/files-tracking/readyToSendPdfsStore';
import FilesTrackingController from './controllers/filesTrackingController';
import PolicyDashboardStore from './ui-stores/files-tracking/policyDashboardStore';
import ReadyToSendEdisStore from './ui-stores/files-tracking/readyToSendEdisStore';
import EmailsDashboardStore from './ui-stores/emails-dashboard/emailsDashboardStore';
import ChangeEffectiveDateStore from './ui-stores/group-info/changeEffectiveDateStore';
import NotificationSettingsStore from './ui-stores/notification-settings/notificationSettingsStore';
import ReviewSubscriberStore from './ui-stores/elections/reviewSubscriberStore';
import ManageSubscriberStore from './ui-stores/elections/manageSubscriberStore';
import ProductLineRateModalStore from './ui-stores/elections/productLineRateModalStore';
import AccountSettingsStore from './ui-stores/account-settings/accountSettingsStore';
import RFQProposalSidebarStore from './ui-stores/rfq-proposal/rfq-proposal-sidebar/rfqProposalSidebarStore';
import RFQProposalChecklistStore from './ui-stores/rfq-proposal/rfq-proposal-checklist/rfqProposalChecklistStore';
import ManageDependentsStore from './ui-stores/elections/manageDependentsStore';
import AddVariantModalStore from './ui-stores/browsing/addVariantModalStore';
import MarkAsStockModalStore from './ui-stores/browsing/markAsStockModal';
import RateRFQModalStore from './ui-stores/proposal-v2/rateRFQModalStore';
import UploadFileModalStore from './ui-stores/proposal-v2/uploadFileModalStore';
import RFQProposalPageStore from './pages/rfq/rfq-response/proposal-rfq/pageStore';
import ProposalPageStore from './pages/proposal/pageStore';

configure({
  computedRequiresReaction: true,
  observableRequiresReaction: true,
});

export class RootStore {
  // Domain
  groupStore: GroupStore;
  agentsStore: AgentsStore;
  /** @deprecated Use ZipCodeDelegate instead */
  geoStore: GeoStore;
  enrollmentStore: EnrollmentStore;
  userStore: UserStore;
  electionStore: ElectionStore;
  csvStore: CsvStore;
  cartStoreV2: CartStoreV2;
  domainStoreV2: DomainStoreV2;
  pdfStore: PdfStore;
  carrierFileStore: CarrierFileStore;
  emailStore: EmailStore;
  exchangeStore: ExchangeStore;
  ediStore: EdiStore;

  // Controllers
  groupInfoController: GroupInfoController;
  groupsDashboardController: GroupsDashboardController;
  filesTrackingController: FilesTrackingController;

  // UI stores
  groupInfoModalStore: GroupInfoModalStore;
  groupNotesModalStore: GroupNotesModalStore;
  browsingModalsStore: BrowsingModalsStore;
  assignAgentsModalStore: AssignAgentsModalStore;
  groupProposalsTableStore: GroupProposalsTableStore;
  webFormStore: WebFormStore;
  groupSidebarStore: GroupSidebarStore;
  searchGroupsModalStore: SearchGroupsModalStore;
  additionalOutputsStore: AdditionalOutputsStore;
  exitModalStore: ExitModalStore;
  employeeElectionsStore: EmployeeElectionsStore;
  editPrimaryModalStore: EditPrimaryModalStore;
  manageDependentsStore: ManageDependentsStore;
  reviewSubmitStore: ReviewSubmitStore;
  attachmentsStore: AttachmentsStore;
  enrollmentCardStore: EnrollmentCardStore;
  insightsStore: InsightsStore;
  bookOfBusinessStore: BookOfBusinessStore;
  summaryTableStore: SummaryTableStore;
  inviteModalStore: InviteModalStore;
  subscriberStore: SubscriberStore;
  additionalAccessStore: AdditionalAccessStore;
  eeClassesStore: EeClassesStore;
  browsingStore: BrowsingStore;
  browsingFilterStore: BrowsingFilterStore;
  browsingCriteriaModalStore: BrowsingCriteriaModalStore;
  saveProposalModalStore: SaveProposalModalStore;
  comparisonStore: ComparisonStore;
  proposalItemsStore: ProposalItemsStore;
  portfolioConfigModalStore: PortfolioConfigModalStore;
  favoriteProposalsStore: FavoriteProposalsStore;
  templatesModalStore: TemplatesModalStore;
  updateProposalModalStore: UpdateProposalModalStore;
  uploadSyncFilesStore: UploadSyncFilesStore;
  triggerRenewalsStore: TriggerRenewalsStore;
  fileExchangeStore: FileExchangeStore;
  browsingPortfolioConfigModalStore: BrowsingPortfolioConfigModalStore;
  readyToSendPdfsStore: ReadyToSendPdfsStore;
  policyDashboardStore: PolicyDashboardStore;
  readyToSendEdisStore: ReadyToSendEdisStore;
  emailsDashboardStore: EmailsDashboardStore;
  notificationSettingsStore: NotificationSettingsStore;
  changeEffectiveDateStore: ChangeEffectiveDateStore;
  reviewSubscriberStore: ReviewSubscriberStore;
  manageSubscriberStore: ManageSubscriberStore;
  productLineRateModalStore: ProductLineRateModalStore;
  accountSettingsStore: AccountSettingsStore;

  customAuthorizedUserStore: CustomAuthorizedUserStore;
  customReplicatedInfoStore: CustomReplicatedInfoStore;
  customPlanRateCapStore: CustomPlanRateCapStore;

  rfqProposalSidebarStore: RFQProposalSidebarStore;
  rfqProposalChecklistStore: RFQProposalChecklistStore;
  addVariantModalStore: AddVariantModalStore;
  markAsStockModalStore: MarkAsStockModalStore;
  rateRFQModalStore: RateRFQModalStore;
  uploadFileModalStore: UploadFileModalStore;

  // Page stores
  rfqProposalPageStore: RFQProposalPageStore;
  proposalPageStore: ProposalPageStore;

  constructor() {
    // Domain stores
    this.groupStore = new GroupStore();
    this.agentsStore = new AgentsStore();
    this.geoStore = new GeoStore();
    this.enrollmentStore = new EnrollmentStore();
    this.userStore = new UserStore();
    this.electionStore = new ElectionStore();
    this.csvStore = new CsvStore();
    this.cartStoreV2 = new CartStoreV2();
    this.domainStoreV2 = new DomainStoreV2();
    this.pdfStore = new PdfStore();
    this.carrierFileStore = new CarrierFileStore();
    this.emailStore = new EmailStore();
    this.exchangeStore = new ExchangeStore();
    this.ediStore = new EdiStore();

    // UI stores
    this.groupInfoModalStore = new GroupInfoModalStore(this);
    this.groupNotesModalStore = new GroupNotesModalStore(this.groupStore);
    this.browsingModalsStore = new BrowsingModalsStore(this);
    this.assignAgentsModalStore = new AssignAgentsModalStore(this);
    this.groupProposalsTableStore = new GroupProposalsTableStore(this);
    this.webFormStore = new WebFormStore(this);
    this.groupSidebarStore = new GroupSidebarStore(this);
    this.searchGroupsModalStore = new SearchGroupsModalStore(this);
    this.additionalOutputsStore = new AdditionalOutputsStore(this);
    this.exitModalStore = new ExitModalStore(this);
    this.employeeElectionsStore = new EmployeeElectionsStore(this);
    this.editPrimaryModalStore = new EditPrimaryModalStore(this);
    this.manageDependentsStore = new ManageDependentsStore(this);
    this.reviewSubmitStore = new ReviewSubmitStore(this);
    this.attachmentsStore = new AttachmentsStore(this);
    this.enrollmentCardStore = new EnrollmentCardStore(this);
    this.insightsStore = new InsightsStore(this);
    this.bookOfBusinessStore = new BookOfBusinessStore(this);
    this.summaryTableStore = new SummaryTableStore(this);
    this.inviteModalStore = new InviteModalStore(this);
    this.subscriberStore = new SubscriberStore(this);
    this.additionalAccessStore = new AdditionalAccessStore(this);
    this.eeClassesStore = new EeClassesStore(this);
    this.browsingStore = new BrowsingStore(this);
    this.browsingFilterStore = new BrowsingFilterStore(this);
    this.browsingCriteriaModalStore = new BrowsingCriteriaModalStore(this);
    this.saveProposalModalStore = new SaveProposalModalStore(this);
    this.comparisonStore = new ComparisonStore(this);
    this.proposalItemsStore = new ProposalItemsStore(this);
    this.portfolioConfigModalStore = new PortfolioConfigModalStore(this);
    this.favoriteProposalsStore = new FavoriteProposalsStore(this);
    this.templatesModalStore = new TemplatesModalStore(this);
    this.updateProposalModalStore = new UpdateProposalModalStore(this);
    this.uploadSyncFilesStore = new UploadSyncFilesStore(this);
    this.triggerRenewalsStore = new TriggerRenewalsStore(this);

    this.fileExchangeStore = new FileExchangeStore(this);
    this.browsingPortfolioConfigModalStore = new BrowsingPortfolioConfigModalStore(this);
    this.readyToSendPdfsStore = new ReadyToSendPdfsStore(this);
    this.policyDashboardStore = new PolicyDashboardStore(this);
    this.readyToSendEdisStore = new ReadyToSendEdisStore(this);
    this.emailsDashboardStore = new EmailsDashboardStore(this);
    this.notificationSettingsStore = new NotificationSettingsStore(this);
    this.changeEffectiveDateStore = new ChangeEffectiveDateStore(this);
    this.reviewSubscriberStore = new ReviewSubscriberStore(this);
    this.manageSubscriberStore = new ManageSubscriberStore(this);
    this.productLineRateModalStore = new ProductLineRateModalStore(this);
    this.accountSettingsStore = new AccountSettingsStore(this);

    this.customAuthorizedUserStore = new CustomAuthorizedUserStore(this);
    this.customReplicatedInfoStore = new CustomReplicatedInfoStore(this);
    this.customPlanRateCapStore = new CustomPlanRateCapStore(this);

    this.rfqProposalSidebarStore = new RFQProposalSidebarStore(this);
    this.rfqProposalChecklistStore = new RFQProposalChecklistStore(this);
    this.addVariantModalStore = new AddVariantModalStore(this);
    this.markAsStockModalStore = new MarkAsStockModalStore(this);
    this.rateRFQModalStore = new RateRFQModalStore(this);
    this.uploadFileModalStore = new UploadFileModalStore(this);

    // (OBSOLETE) Controller stores
    this.groupInfoController = new GroupInfoController(this);
    this.groupsDashboardController = new GroupsDashboardController(this);
    this.filesTrackingController = new FilesTrackingController(this);

    // Page stores
    this.rfqProposalPageStore = new RFQProposalPageStore(this.proposalItemsStore);
    this.proposalPageStore = new ProposalPageStore(this.proposalItemsStore);
  }
}

const rootStore = new RootStore();

export function getRootStoreInstance(): RootStore {
  return rootStore;
}

const RootContext = createContext(rootStore);

export const useRootStore = (): RootStore => {
  return useContext(RootContext);
};

export { RootContext };
